import React, { useState, useRef, useCallback, useEffect } from "react";
import Autosuggest from "react-autosuggest";
import PropTypes from "prop-types";
import styles from "../../../../css/form/multiselect.module.css";
import { classNames } from "../../../../utils/helpers";
import { MdErrorOutline } from "react-icons/md";
const SelectAndSearch = (props) => {
  const {
    handleChange,
    options,
    title = "",
    placeholder = "",
    errors = "",
    isError = false,
    required = false,
    onReset = false,
  } = props;
  const selectRef = useRef(null);
  const [value, setValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  const handleClickOutside = useCallback((e) => {
    if (selectRef.current && !selectRef.current.contains(e.target)) {
      setSuggestions([]);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    if (!value) {
      handleChange("");
    }
  }, [value]);

  const handleReset = () => {
    setValue("");
    handleChange("");
  };

  useEffect(() => {
    if (onReset) {
      handleReset();
    }
  }, [onReset]);

  const onChange = (event, { newValue }) => {
    setValue(newValue);
  };

  const getSuggestions = (valueToFilter) => {
    const inputValue = (valueToFilter || "").trim().toLowerCase();
    if (!options || options.length === 0) return [];
    return options.filter((option) =>
      option.label.toLowerCase().includes(inputValue),
    );
  };

  const onSuggestionsFetchRequested = ({ value }) => {
    setSuggestions(getSuggestions(value));
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const onSuggestionSelected = (event, { suggestion }) => {
    if (suggestion) {
      handleChange(suggestion.value);
      setValue(suggestion.label);
      setSuggestions([]);
    }
  };

  const inputProps = {
    placeholder: placeholder,
    value: value,
    onChange: onChange,
    onFocus: () => onSuggestionsFetchRequested({ value }),
  };

  const getSuggestionValue = (suggestion) => suggestion.value;

  const renderSuggestion = (suggestion) => <div>{suggestion.label}</div>;

  const themeCustom = {
    container: "w-full",
    suggestionsContainer:
      "absolute mt-1 bg-white rounded-lg max-h-60 overflow-y-auto w-full z-10",
    suggestionsList: "list-none p-2 m-0",
    suggestion:
      "px-3 py-2 cursor-pointer rounded-md hover:bg-blue-50 hover:border-blue-100 transition-all duration-200 capitalize sm:text-sm",
    suggestionHighlighted: "bg-blue-500 text-gray-800",
  };

  const renderInputComponent = (inputProps) => (
    <div className="relative w-full">
      <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none">
        search
      </span>

      <input
        type="text"
        {...inputProps}
        className={classNames(
          styles.multiselect,
          "focus:outline-none",
          isError && styles.error,
        )}
      />

     
       {isError && errors && (
        <MdErrorOutline
          size={20}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#DC3545]"
        />
      )}

      <button
        className="border-none !bg-transparent flex items-center absolute right-6 top-1/2 -translate-y-1/2"
        onClick={handleReset}
      >
        <span className="material-symbols-outlined text-[#6c757d]">close</span>
      </button>
    
    </div>
  );

  return (
    <div className="flex flex-col py-2 w-full relative" ref={selectRef}>
      <label className="text-gray-700 font-semibold text-sm flex justify-start align-top mb-2">
        {title}
        {required && <span className="text-red-700 mx-1">*</span>}
      </label>

      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        renderInputComponent={renderInputComponent}
        inputProps={inputProps}
        theme={themeCustom}
        shouldRenderSuggestions={() => true}
        onSuggestionSelected={onSuggestionSelected}
      />

      {isError && errors && (
        <div className="text-[#DC3545] text-[14px] mt-1">{errors}</div>
      )}
    </div>
  );
};

SelectAndSearch.propTypes = {
  handleChange: PropTypes.func,
  options: PropTypes.array,
  title: PropTypes.any,
  placeholder: PropTypes.any,
  errors: PropTypes.any,
  isError: PropTypes.bool,
  required: PropTypes.bool,
  values: PropTypes.any,
  onReset: PropTypes.bool,
};

export default SelectAndSearch;
